import dayjs from "dayjs";
import { takeLatest, call, put, select } from "redux-saga/effects";
import { makeTextFileForDownload } from "utils/fileManager/file-manager";
import { isStateOrAction } from "utils/functions";
import YmsNotification from "utils/functions/notification";
import { getTimeZoneName } from "utils/intl/getLocale";
import { translate } from "utils/intl/IntlProvider";
import { getError } from "utils/requests/api.errors";
import * as Api from "./report-api";
import { Types, Creators } from "./report-duck";
import { IMovementItems } from "./report-model";

const dateFormat = "YYYY-MM-DD";
export const getReportFromState = (state) => state.Report;

export const extractDocuments = (item, label) => {
  const documentsColumns = item?.map((x) => `${x.type} ${label}`);
  const documentsRows = item?.map((x) => x.value);
  return documentsRows.reduce((result, field, index) => {
    if (!result[documentsColumns[index]]) {
      result[documentsColumns[index]] = [];
    }
    result[documentsColumns[index]].push(field);
    return result;
  }, {});
};

export function* onReportFetchAsync(action: any) {
  try {
    const initialEventTimeFormat = dayjs(action.body._minEventTime).format("DD-MM-YYYY");
    const finalEventTimeFormat = dayjs(action.body._maxEventTime).format("DD-MM-YYYY");
    yield put(Creators.reportShowLoading());
    yield put(Creators.reportShowLoadingRegisters());
    const resp = yield call(Api.getReport, action.body);
    const nameFile = yield translate("name_file_csv");
    yield makeTextFileForDownload(resp.data, `${nameFile}_${initialEventTimeFormat}_${finalEventTimeFormat}`, "csv");
    const requestSuccessfullyMessage = yield translate("CSV_exported_successfully!");
    yield YmsNotification({
      type: "success",
      description: requestSuccessfullyMessage,
    });
  } catch (error) {
    console.error(error);
    yield put(Creators.reportHideLoadingRegisters());
    const requestErrorMessage = yield getError(error) ?? translate("there_was_an_error_exporting_CSV");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  } finally {
    yield put(Creators.reportHideLoading());
    yield put(Creators.reportHideLoadingRegisters());
  }
}

export function* checkFilterStateIsNull(state, filterState, filterAction) {
  if (filterState === null) {
    yield put(Creators.reportChangeFieldValue(state, filterAction));
  }
}

export function* onReportRegisterFetchAsync(action: any) {
  try {
    yield put(Creators.reportShowLoading());
    const report = yield select(getReportFromState);
    const INBOUND = yield translate("inbound");
    const OUTBOUND = yield translate("outbound");

    const initialEventTimeState = report.filter.initialEventTime;
    const finalEventTimeState = report.filter.finalEventTime;
    const structureIdState = report.filter.structureId;
    const locationState = report.filter.location;
    const filterPlateState = report.filter.plate;

    const filterTrailerPlateState = report.filter.trailerPlate;
    const filterDocumentState = report.filter.document;

    const initialEventTimeAction = dayjs(action.body?.initialEventTime).format(dateFormat);
    const finalEventTimeAction = dayjs(action.body?.finalEventTime).format(dateFormat);
    const structureIdAction = action.body?.structureId;
    const locationAction = action.body?.locations;
    const locationTimeZone = getTimeZoneName();
    const filterPlateAction = action.body?.plate;
    const filterTrailerPlateAction = action.body?.trailerPlate;
    const filterDocumentAction = action.body?.document;

    const initialEventTimeFormat = isStateOrAction(initialEventTimeState, initialEventTimeAction);
    const finalEventTimeFormat = isStateOrAction(finalEventTimeState, finalEventTimeAction);
    const structureIdData = isStateOrAction(structureIdState, structureIdAction);
    const locationsData = isStateOrAction(locationState, locationAction);
    const filterPlateData = isStateOrAction(filterPlateState, filterPlateAction);
    const filterTrailerPlateData = isStateOrAction(filterTrailerPlateState, filterTrailerPlateAction);
    const filterDocumentData = isStateOrAction(filterDocumentState, filterDocumentAction);

    yield put(Creators.reportShowLoadingRegisters());
    const resp = yield call(Api.getReportFetchToTable, {
      _size: report.filter.itemsByPage,
      _page: report.filter.pageIndex,
      structureId: structureIdData,
      locations: locationsData,
      _minEventTime: initialEventTimeFormat,
      _maxEventTime: finalEventTimeFormat,
      locationTimeZone,
      plate: filterPlateData,
      trailerPlate: filterTrailerPlateData,
      document: filterDocumentData,
    });

    yield checkFilterStateIsNull("document", filterDocumentState, filterDocumentAction);
    yield checkFilterStateIsNull("plate", filterPlateState, filterPlateAction);
    yield checkFilterStateIsNull("trailerPlate", filterTrailerPlateState, filterTrailerPlateAction);
    yield checkFilterStateIsNull("initialEventTime", initialEventTimeState, initialEventTimeAction);
    yield checkFilterStateIsNull("finalEventTime", finalEventTimeState, finalEventTimeAction);
    yield checkFilterStateIsNull("location", locationState, locationAction);
    yield checkFilterStateIsNull("structureId", structureIdState, structureIdAction);

    const reportData = resp.data;
    const dataItems = resp.data.items.map((item) => {
      const driversName = item.drivers?.map((x) => x.name);
      const cpfDocuments = item.drivers?.map((x) => x.cpf);

      return {
        structure: item.structure,
        location: item.location,
        locationCode: item.locationCode,
        vehiclePlate: item.vehiclePlate,
        inboundTrailersPlates: item.inboundTrailersPlates,
        carrier: item.carrier,
        inboundProcessDescription: item.inboundProcessDescription,
        outboundProcessDescription: item.outboundProcessDescription,
        inboundObservation: item.inboundObservation,
        checkinEventTime: item.checkinEventTime,
        checkinSource: item.checkinSource,
        radiusInEventTime: item.radiusInEventTime,
        inboundEventTime: item.inboundEventTime,
        outboundEventTime: item.outboundEventTime,
        outboundTrailersPlates: item.outboundTrailersPlates,
        outboundObservation: item.outboundObservation,
        vehicleStatus: item.vehicleStatus,
        vehicleName: item.vehicleName,
        vehicleReason: item.vehicleReason,
        driverStatus: item.driverStatus,
        driverReason: item.driverReason,
        verified: item.verified,
        trailerType: item.trailerType,
        saleChannelDescription: item.saleChannelDescription,
        driver: driversName,
        cpf: cpfDocuments,
        ...extractDocuments(item.inboundDocuments, INBOUND),
        ...extractDocuments(item.outboundDocuments, OUTBOUND),
      };
    });
    yield put(Creators.reportFetchRegister(dataItems, reportData.totalItems, reportData.inboundDocumentsTypes, reportData.outboundDocumentsTypes));
  } catch (error) {
    console.error(error);
    yield put(Creators.reportHideLoadingRegisters());
    const requestErrorMessage = yield getError(error) ?? translate("report_could_not_be_loaded");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  } finally {
    yield put(Creators.reportHideLoading());
    yield put(Creators.reportHideLoadingRegisters());
  }
}

export function* onMovementReportFetchAsync(action: any) {
  try {
    const initialEventTimeFormat = dayjs(action.body._minEventTime).format("DD-MM-YYYY");
    const finalEventTimeFormat = dayjs(action.body._maxEventTime).format("DD-MM-YYYY");
    yield put(Creators.reportMovementsShowLoading());
    yield put(Creators.reportMovementsShowLoadingRegisters());
    const resp = yield call(Api.getMovementReport, action.body);
    const nameFile = yield translate("movement_report_name_file_csv");
    yield makeTextFileForDownload(resp.data, `${nameFile}_${initialEventTimeFormat}_${finalEventTimeFormat}`, "csv");
    const requestSuccessfullyMessage = yield translate("CSV_exported_successfully!");
    yield YmsNotification({
      type: "success",
      description: requestSuccessfullyMessage,
    });
  } catch (error) {
    console.error(error);
    yield put(Creators.reportMovementsHideLoadingRegisters());
    const requestErrorMessage = yield getError(error) ?? translate("there_was_an_error_exporting_CSV");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  } finally {
    yield put(Creators.reportMovementsHideLoading());
    yield put(Creators.reportMovementsHideLoadingRegisters());
  }
}

export function* checkIfMovementFilterStateIsNull(state, filterState, filterAction) {
  if (filterState === null) {
    yield put(Creators.reportMovementChangeFieldValue(state, filterAction));
  }
}

export function* onReportMovementsRegisterFetchAsync(action: any) {
  try {
    yield put(Creators.reportMovementsShowLoading());
    const report = yield select(getReportFromState);

    const initialEventTimeState = report.movementFilter.initialEventTime;
    const finalEventTimeState = report.movementFilter.finalEventTime;
    const structureIdState = report.movementFilter.structureId;
    const locationState = report.movementFilter.location;
    const filterPlateState = report.movementFilter.plate;
    const filterTrailerPlateState = report.movementFilter.trailerPlate;
    const filterDtDocumentState = report.movementFilter.dtDocument;

    const initialEventTimeAction = dayjs(action.body?.initialEventTime).format(dateFormat);
    const finalEventTimeAction = dayjs(action.body?.finalEventTime).format(dateFormat);
    const structureIdAction = action.body?.structureId;
    const locationAction = action.body?.locations;
    const locationTimeZone = getTimeZoneName();
    const filterPlateAction = action.body?.plate;
    const filterTrailerPlateAction = action.body?.trailerPlate;
    const filterDtDocumentAction = action.body?.dtDocument;

    const initialEventTimeFormat = isStateOrAction(initialEventTimeState, initialEventTimeAction);
    const finalEventTimeFormat = isStateOrAction(finalEventTimeState, finalEventTimeAction);
    const structureIdData = isStateOrAction(structureIdState, structureIdAction);
    const locationsData = isStateOrAction(locationState, locationAction);
    const filterPlateData = isStateOrAction(filterPlateState, filterPlateAction);
    const filterTrailerPlateData = isStateOrAction(filterTrailerPlateState, filterTrailerPlateAction);
    const filterDtDocumentData = isStateOrAction(filterDtDocumentState, filterDtDocumentAction);

    yield put(Creators.reportMovementsShowLoadingRegisters());
    const resp = yield call(Api.getMovementReportFetchToTable, {
      _size: report.movementFilter.itemsByPage,
      _page: report.movementFilter.pageIndex,
      structureId: structureIdData,
      locations: locationsData,
      _minEventTime: initialEventTimeFormat,
      _maxEventTime: finalEventTimeFormat,
      locationTimeZone,
      plate: filterPlateData,
      trailerPlate: filterTrailerPlateData,
      dtDocument: filterDtDocumentData,
    });

    yield checkIfMovementFilterStateIsNull("dtDocument", filterDtDocumentState, filterDtDocumentAction);
    yield checkIfMovementFilterStateIsNull("plate", filterPlateState, filterPlateAction);
    yield checkIfMovementFilterStateIsNull("trailerPlate", filterTrailerPlateState, filterTrailerPlateAction);
    yield checkIfMovementFilterStateIsNull("initialEventTime", initialEventTimeState, initialEventTimeAction);
    yield checkIfMovementFilterStateIsNull("finalEventTime", finalEventTimeState, finalEventTimeAction);
    yield checkIfMovementFilterStateIsNull("location", locationState, locationAction);
    yield checkIfMovementFilterStateIsNull("structureId", structureIdState, structureIdAction);

    const reportData = resp.data;
    const dataItems = reportData.items.map((item: IMovementItems) => {
      return {
        structure: item.structure,
        location: item.location,
        vehiclePlate: item.vehiclePlate,
        inboundTrailersPlates: item.inboundTrailersPlates,
        outboundTrailersPlates: item.outboundTrailersPlates,
        dtDocument: item.dtDocument,
        vehicleCheckinEventTime: item.vehicleCheckinEventTime,
        checkinSource: item.checkinSource,
        radiusInEventTime: item.radiusInEventTime,
        vehicleReleasedEventTime: item.vehicleReleasedEventTime,
        requestVehicleForGateEventTime: item.requestVehicleForGateEventTime,
        inboundEventTime: item.inboundEventTime,
        requestDockEntryEventTime: item.requestDockEntryEventTime,
        finishedOperationEventTime: item.finishedOperationEventTime,
        outboundEventTime: item.outboundEventTime,
        rangeOutboundEventTime: item.rangeOutboundEventTime,
        canceledAt: item.canceledAt,
      };
    });
    yield put(Creators.reportMovementFetchRegister(dataItems, reportData.totalItems));
  } catch (error) {
    console.error(error);
    yield put(Creators.reportMovementsHideLoading());
    const requestErrorMessage = yield getError(error) ?? translate("report_could_not_be_loaded");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  } finally {
    yield put(Creators.reportMovementsHideLoading());
    yield put(Creators.reportMovementsHideLoadingRegisters());
  }
}

export default function* ReportSagas() {
  yield takeLatest(Types.REPORT_FETCH_ASYNC, onReportFetchAsync);
  yield takeLatest(Types.REPORT_MOVEMENT_FETCH_ASYNC, onMovementReportFetchAsync);
  yield takeLatest(Types.REPORT_FETCH_REGISTER_ASYNC, onReportRegisterFetchAsync);
  yield takeLatest(Types.REPORT_MOVEMENTS_FETCH_REGISTER_ASYNC, onReportMovementsRegisterFetchAsync);
}
